import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack } from '../../components/icons';

interface RefOption {
  id: number;
  name: string;
}

interface EmployeePositionRow {
  id: number;
  start_date: string;
  end_date: string | null;
  employee: { id: number; user: { full_name: string } | null } | null;
}

interface PositionDetailRecord {
  id: number;
  position_code: string;
  position_title: string;
  budgeted_fte: number;
  status: string;
  job: { id: number; job_title: string } | null;
  org_unit: { id: number; unit_name: string } | null;
  location: { id: number; location_name: string } | null;
  reports_to: { id: number; position_title: string } | null;
  pay_grade: RefOption | null;
  work_shift: RefOption | null;
  employee_positions: EmployeePositionRow[];
}

const TABS = ['details', 'employees'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { details: 'Details', employees: 'Employees' };

export function PositionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('details');
  const [position, setPosition] = useState<PositionDetailRecord | null>(null);

  useEffect(() => {
    api.get<PositionDetailRecord>(`/positions/${id}`).then((res) => setPosition(res.data));
  }, [id]);

  if (!position) return null;

  return (
    <>
      <header className="page-header">
        <h1>{position.position_title}</h1>
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/hr/positions')} />
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {tab === 'details' && (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item-label">Position Code</span>
              <span className="detail-item-value">{position.position_code}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Position Title</span>
              <span className="detail-item-value">{position.position_title}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Job</span>
              <span className="detail-item-value">{position.job?.job_title}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Org Unit</span>
              <span className="detail-item-value">{position.org_unit?.unit_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Location</span>
              <span className={`detail-item-value${position.location ? '' : ' empty'}`}>
                {position.location?.location_name ?? 'Not set'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Reports To</span>
              <span className={`detail-item-value${position.reports_to ? '' : ' empty'}`}>
                {position.reports_to?.position_title ?? 'Not set'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Pay Grade</span>
              <span className="detail-item-value">{position.pay_grade?.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Work Shift</span>
              <span className="detail-item-value">{position.work_shift?.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Budgeted FTE</span>
              <span className="detail-item-value">{position.budgeted_fte}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Status</span>
              <span className="detail-item-value">
                <span className="badge badge-neutral">{position.status}</span>
              </span>
            </div>
          </div>
        )}

        {tab === 'employees' && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Start</th>
                  <th>End</th>
                </tr>
              </thead>
              <tbody>
                {position.employee_positions.map((row) => (
                  <tr key={row.id}>
                    <td>{row.employee?.user?.full_name ?? '—'}</td>
                    <td className="muted">{row.start_date}</td>
                    <td className="muted">{row.end_date ?? 'Current'}</td>
                  </tr>
                ))}
                {position.employee_positions.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted center">
                      No employees have held this position yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
